import React, { useState, useCallback, useMemo, useEffect } from 'react';
import Modal from '../common/Modal';
import ModalContent from '../common/ModalContent';
import { Button } from '../common';
import { Heading, Text, Typography } from '../common/Typography';
import { getGenderTextColor } from '../../utils/genderColors';
import { getQualifiedTeams, identifyTieBreakerSituations } from '../../utils/tournamentStandings';
import { formatPlayerName } from '../../utils/nameUtils';
import { formatTournamentEntryLabel } from '../../utils/tournamentFormat';

const VALID_BRACKET_SIZES = [2, 4, 8, 16];

/**
 * Modal for host to select which teams qualify for knockout bracket
 */
const GenerateKnockoutModal = ({
  isOpen,
  onClose,
  groupStandings,
  progressionTopN = 2,
  progressionBestLosers = false,
  bestLosersRankingOrder = null,
  onGenerate,
  generating = false
}) => {
  const [selected, setSelected] = useState({});
  const [includeBronze, setIncludeBronze] = useState(false);

  const allTeams = useMemo(() => {
    if (!groupStandings) return [];
    const teams = [];
    groupStandings.forEach(({ groupName, standings }) => {
      standings.forEach((row) => {
        teams.push({
          teamId: row.teamId,
          groupName,
          position: row.position,
          seedLabel: `${groupName}${row.position}`,
          ...row
        });
      });
    });
    return teams;
  }, [groupStandings]);

  const toggleTeam = useCallback((teamId) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[teamId]) {
        delete next[teamId];
      } else {
        next[teamId] = true;
      }
      return next;
    });
  }, []);

  const selectedCount = Object.keys(selected).length;
  const isValidBracket = VALID_BRACKET_SIZES.includes(selectedCount);
  const selectedTeamsOrdered = useMemo(() => {
    return allTeams.filter((t) => selected[t.teamId]);
  }, [allTeams, selected]);

  const suggestedQualifiers = useMemo(() => {
    if (!groupStandings?.length) return [];
    return getQualifiedTeams(groupStandings, progressionTopN, progressionBestLosers, bestLosersRankingOrder);
  }, [groupStandings, progressionTopN, progressionBestLosers, bestLosersRankingOrder]);

  const tieSituations = useMemo(() => {
    if (!groupStandings?.length) return [];
    return identifyTieBreakerSituations(groupStandings, progressionTopN);
  }, [groupStandings, progressionTopN]);

  const tiedTeamIds = useMemo(() => {
    const ids = new Set();
    tieSituations.forEach((s) => s.tiedTeams.forEach((t) => ids.add(t.teamId)));
    return ids;
  }, [tieSituations]);

  // Pre-select suggested qualifiers when modal opens (review step based on groups section settings)
  // Exclude tied teams from pre-selection so host must consciously choose – avoids missing tie-breakers
  useEffect(() => {
    if (!isOpen) {
      setSelected({});
      return;
    }
    if (suggestedQualifiers.length > 0) {
      const next = {};
      suggestedQualifiers.forEach((t) => {
        if (!tiedTeamIds.has(t.teamId)) {
          next[t.teamId] = true;
        }
      });
      setSelected(next);
    }
  }, [isOpen, suggestedQualifiers, tiedTeamIds]);

  const handleAutoFill = useCallback(() => {
    const next = {};
    suggestedQualifiers.forEach((t) => {
      if (!tiedTeamIds.has(t.teamId)) {
        next[t.teamId] = true;
      }
    });
    setSelected(next);
  }, [suggestedQualifiers, tiedTeamIds]);

  const hasManualChanges = useMemo(() => {
    const suggestedIds = new Set(suggestedQualifiers.map((t) => t.teamId));
    const selectedIds = new Set(Object.keys(selected));
    if (suggestedIds.size !== selectedIds.size) return true;
    return [...selectedIds].some((id) => !suggestedIds.has(id));
  }, [suggestedQualifiers, selected]);

  const handleGenerate = async () => {
    if (!isValidBracket) return;

    // Compute qualification rank per group (best group-stage position = qualRank 1).
    // This ensures A3 who won a tiebreaker is labelled A2, not A3.
    const selectedByGroup = {};
    selectedTeamsOrdered.forEach((t) => {
      const g = t.groupName || '';
      if (!selectedByGroup[g]) selectedByGroup[g] = [];
      selectedByGroup[g].push(t);
    });
    Object.values(selectedByGroup).forEach((arr) => arr.sort((a, b) => (a.position ?? 0) - (b.position ?? 0)));

    const qualified = selectedTeamsOrdered.map((t) => {
      const g = t.groupName || '';
      const groupArr = selectedByGroup[g];
      const qualRank = groupArr.findIndex((gt) => gt.teamId === t.teamId) + 1;
      return {
        teamId: t.teamId,
        groupName: g,
        qualRank,
        seedLabel: g ? `${g}${qualRank}` : String(qualRank),
      };
    });

    await onGenerate(qualified, includeBronze);
    onClose();
  };

  const handleClose = useCallback(() => {
    setSelected({});
    setIncludeBronze(false);
    onClose();
  }, [onClose]);

  // Bronze only applies when there are semi-finals (4+ teams)
  const canHaveBronze = selectedCount >= 4;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} closeOnBackdropClick={!generating}>
      <ModalContent size="xlarge" padding={false} className="flex min-h-0 flex-col overflow-hidden">
        <div className="p-6 border-b border-border-medium">
          <div className="flex items-center justify-between">
            <Heading level={2}>Generate Knockout Bracket</Heading>
            <button
              onClick={handleClose}
              disabled={generating}
              className="text-text-muted hover:text-text-primary transition-colors disabled:opacity-50"
            >
              ✕
            </button>
          </div>
          <Text className="text-text-secondary mt-2 block">
            Review the qualifying teams below based on your progression settings (top {progressionTopN} per group{progressionBestLosers ? ', plus best losers' : ''}). Adjust if needed, then generate the bracket.
          </Text>
          {tieSituations.length > 0 && (
            <div className="mt-2 px-3 py-2 rounded bg-warning/10 border border-warning/30">
              <Text className="text-warning font-medium">
                {tieSituations.length} group{tieSituations.length > 1 ? 's have' : ' has'} tied teams at the qualification boundary – review and pick qualifiers below.
              </Text>
            </div>
          )}
          {suggestedQualifiers.length > 0 && VALID_BRACKET_SIZES.includes(suggestedQualifiers.length) && (
            <Button
              variant="secondary"
              size="small"
              onClick={handleAutoFill}
              disabled={!hasManualChanges}
              className="mt-2"
            >
              Reset to suggested qualifiers
            </Button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {groupStandings?.map(({ groupName, groupId, standings }) => {
              const groupTieSituation = tieSituations.find(
                (s) => s.groupName === groupName || s.groupId === groupId
              );
              return (
              <div key={groupName} className="border border-border-medium rounded-lg overflow-hidden">
                <div className="bg-surface-subtle px-4 py-2 font-medium">
                  Group {groupName}
                </div>
                {groupTieSituation && (
                  <div className="bg-warning/10 border-b border-warning/30 px-4 py-3">
                    <Typography variant="body" className="font-medium text-warning">
                      Tie-breaker required
                    </Typography>
                    <Text className="text-sm text-text-secondary mt-1 block">
                      Positions {groupTieSituation.tiedTeams.map((t) => t.position).sort((a, b) => a - b).join(', ')} are tied
                      ({groupTieSituation.sharedStats.wins} wins, {groupTieSituation.sharedStats.pointDifference >= 0 ? '+' : ''}{groupTieSituation.sharedStats.pointDifference} point diff).
                      {groupTieSituation.headToHeadAvailable
                        ? ' Head-to-head result available below – use it to decide, or pick manually.'
                        : ' Pick which team(s) qualify using the checkboxes.'}
                    </Text>
                    {groupTieSituation.headToHeadAvailable && (
                      <div className="mt-2 text-sm text-text-secondary">
                        {groupTieSituation.tiedTeams.flatMap((teamA, i) =>
                          groupTieSituation.tiedTeams.slice(i + 1).map((teamB) => {
                            const aVsB = teamA.headToHead?.[teamB.teamId];
                            const bVsA = teamB.headToHead?.[teamA.teamId];
                            if (!aVsB || !bVsA) return [];
                            const aWins = aVsB.wins;
                            const bWins = bVsA.wins;
                            const winner = aWins > bWins ? teamA : bWins > aWins ? teamB : null;
                            const teamAName =
                              formatTournamentEntryLabel(teamA.team, !teamA.team?.player2) || 'Team A';
                            const teamBName =
                              formatTournamentEntryLabel(teamB.team, !teamB.team?.player2) || 'Team B';
                            return [
                              <div key={`${teamA.teamId}-${teamB.teamId}`} className="mt-1">
                                Head-to-head: {teamAName} vs {teamBName} —{' '}
                                {winner
                                  ? `${formatTournamentEntryLabel(winner.team, !winner.team?.player2)} won`
                                  : 'Draw (same wins)'}
                              </div>
                            ];
                          })
                        )}
                      </div>
                    )}
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border-medium">
                        <th className="text-left py-2 px-2 w-10">
                          <span className="sr-only">Select</span>
                        </th>
                        <th className="text-left py-2 px-2 font-medium text-text-secondary w-8">#</th>
                        <th className="text-left py-2 px-2 font-medium text-text-secondary">Team</th>
                        <th className="text-center py-2 px-2 font-medium text-text-secondary w-12">P</th>
                        <th className="text-center py-2 px-2 font-medium text-text-secondary w-12">W</th>
                        <th className="text-center py-2 px-2 font-medium text-text-secondary w-12">L</th>
                        <th className="text-center py-2 px-2 font-medium text-text-secondary w-12">PF</th>
                        <th className="text-center py-2 px-2 font-medium text-text-secondary w-12">PA</th>
                        <th className="text-center py-2 px-2 font-medium text-text-secondary w-12" title="Points for / matches">
                          Avg
                        </th>
                        <th className="text-center py-2 px-2 font-medium text-text-secondary w-12">+/-</th>
                      </tr>
                    </thead>
                    <tbody>
                      {standings.map((row) => {
                        const isChecked = !!selected[row.teamId];
                        const isQualifying = row.position <= progressionTopN;
                        const isTied = tiedTeamIds.has(row.teamId);
                        return (
                          <tr
                            key={row.teamId}
                            onClick={() => toggleTeam(row.teamId)}
                            className={`border-b border-border-subtle last:border-0 cursor-pointer hover:bg-surface-subtle transition-colors ${
                              isChecked ? 'bg-primary/10' : ''
                            } ${isQualifying && !isChecked ? 'bg-success/5' : ''} ${
                              isTied ? 'bg-warning/15 border-l-4 border-l-warning' : ''
                            }`}
                          >
                            <td className="py-2 px-2" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => toggleTeam(row.teamId)}
                                className="rounded border-border-medium text-primary focus:ring-primary"
                              />
                            </td>
                            <td className="py-2 px-2 font-medium text-text-muted">{row.position}</td>
                            <td className="py-2 px-2">
                              {row.team?.player2 ? (
                                <>
                                  <span className={getGenderTextColor(row.team?.player1?.gender || '')}>
                                    {formatPlayerName(row.team?.player1)}
                                  </span>
                                  {' & '}
                                  <span className={getGenderTextColor(row.team?.player2?.gender || '')}>
                                    {formatPlayerName(row.team?.player2)}
                                  </span>
                                </>
                              ) : (
                                <span className={getGenderTextColor(row.team?.player1?.gender || '')}>
                                  {formatTournamentEntryLabel(row.team, true)}
                                </span>
                              )}
                            </td>
                            <td className="py-2 px-2 text-center">{row.matchesPlayed ?? 0}</td>
                            <td className="py-2 px-2 text-center font-medium text-success">{row.wins ?? 0}</td>
                            <td className="py-2 px-2 text-center text-text-muted">{row.losses ?? 0}</td>
                            <td className="py-2 px-2 text-center">{row.pointsFor ?? 0}</td>
                            <td className="py-2 px-2 text-center">{row.pointsAgainst ?? 0}</td>
                            <td className="py-2 px-2 text-center text-text-secondary">
                              {(row.matchesPlayed ?? 0) > 0
                                ? (row.avgPointsPerMatch ?? (row.pointsFor ?? 0) / row.matchesPlayed).toFixed(1)
                                : '—'}
                            </td>
                            <td
                              className={`py-2 px-2 text-center font-medium ${
                                (row.pointDifference ?? 0) >= 0 ? 'text-success' : 'text-danger'
                              }`}
                            >
                              {(row.pointDifference ?? 0) >= 0 ? '+' : ''}
                              {row.pointDifference ?? 0}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <Text className="text-xs text-text-muted px-4 py-2 block bg-surface-subtle/50">
                  P=Played, W=Wins, L=Losses, PF=Points For, PA=Points Against, +/-=Point Diff. Click row to select.
                </Text>
              </div>
            );
            })}
          </div>
        </div>

        <div className="p-6 border-t border-border-medium flex items-center justify-between gap-4">
          <div className="flex flex-col gap-2">
            {selectedCount > 0 && !isValidBracket && (
              <Typography variant="body" className="text-warning">
                Select 2, 4, 8, or 16 teams (currently {selectedCount})
              </Typography>
            )}
            {isValidBracket && (
              <Typography variant="body" className="text-success">
                {selectedCount} teams selected – bracket ready
              </Typography>
            )}
            {canHaveBronze && (
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={includeBronze}
                  onChange={(e) => setIncludeBronze(e.target.checked)}
                  className="rounded border-border-medium text-primary focus:ring-primary"
                />
                <Text className="text-sm">Include 3rd place match (SF losers play for bronze)</Text>
              </label>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={handleClose} disabled={generating}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleGenerate}
              disabled={!isValidBracket || generating}
            >
              {generating ? 'Generating...' : 'Generate Bracket'}
            </Button>
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default GenerateKnockoutModal;
